/**
 * Mock data for the Easement Knowledge Hub & Professional Directory (/resources).
 *
 * Everything here is intentionally shaped like a future database response so the
 * UI can be re-pointed at a backend query later without changing component props.
 */

export type ServiceType =
  "Title Abstractor" | "ALTA Surveyor" | "Real Estate Attorney" | "Abstract & Search Company";

export type DirectoryListing = {
  id: string;
  businessName: string;
  serviceType: ServiceType;
  state: string;
  counties: string[];
  verified: boolean;
  blurb: string;
  /** Optional public contact address used to prefill the request form. */
  contactEmail?: string;
  yearsInBusiness?: number;
};

export type EasementTopic = {
  id: string;
  title: string;
  shortLabel: string;
  summary: string;
  lookFor: string[];
  riskNote: string;
  keywords: string[];
  /** Grouping used by the Knowledge Hub category rail. */
  category?: EasementTopicCategory;
};

export type EasementTopicCategory =
  | "Core easement types"
  | "Conservation & land protection"
  | "Infrastructure & corridors"
  | "Title, records & disputes";

export const EASEMENT_TOPIC_CATEGORIES: EasementTopicCategory[] = [
  "Core easement types",
  "Conservation & land protection",
  "Infrastructure & corridors",
  "Title, records & disputes",
];

export const SERVICE_TYPES: ServiceType[] = [
  "Title Abstractor",
  "ALTA Surveyor",
  "Real Estate Attorney",
  "Abstract & Search Company",
];

export const EASEMENT_TOPICS: EasementTopic[] = [
  {
    id: "utility",
    title: "Utility easements",
    shortLabel: "Utility",
    summary:
      "A utility easement gives a public or private utility company the right to install, access, and maintain infrastructure across part of your lot. Power lines, water and sewer mains, gas, fiber, and telecom. The right belongs to the utility, not to you, and it usually survives every future sale of the property.",
    lookFor: [
      "The recorded width and location in the deed or plat (for example, a 10-foot strip along the rear lot line).",
      "Whether the recorded corridor actually matches where poles, pedestals, transformers, or manholes physically sit today.",
      "Blanket easements with no fixed location. These can encumber the entire parcel until the utility builds.",
      "Maintenance and vegetation clauses that let crews clear trees or structures inside the corridor.",
    ],
    riskNote:
      "Public records describe the legal right; the physical equipment describes the practical one. When the two disagree, the recorded corridor normally controls, but the utility can still act around its installed facilities. Any structure you place inside the corridor may have to be removed at your expense.",
    keywords: ["utility", "power", "sewer", "water", "telecom", "blanket", "right-of-way"],
  },
  {
    id: "appurtenant",
    title: "Appurtenant easements",
    shortLabel: "Appurtenant",
    summary:
      "An appurtenant easement attaches to the land itself rather than to a person. One parcel (the dominant estate) holds a right to use part of a neighboring parcel (the servient estate). Most commonly a shared driveway, an access road, or an ingress/egress corridor to a landlocked lot. It transfers automatically with the property.",
    lookFor: [
      "Which parcel is benefited and which is burdened. Buying the burdened side means inheriting the obligation.",
      "Shared driveway and private road maintenance agreements, and how costs are split.",
      "Whether access to the lot depends on someone else's land; landlocked parcels need a recorded route.",
      "Scope limits. An easement for residential access may not permit commercial or construction traffic.",
    ],
    riskNote:
      "Because these run with the land, a buyer cannot negotiate them away at closing. Verify that any access you are relying on is recorded; a driveway that has simply always been used is not the same as a recorded right.",
    keywords: ["appurtenant", "driveway", "ingress", "egress", "access", "landlocked"],
  },
  {
    id: "prescriptive",
    title: "Prescriptive easements",
    shortLabel: "Prescriptive",
    summary:
      "A prescriptive easement is created by use rather than by a document. When someone uses part of a property openly, continuously, and without permission for the period set by state law, they may acquire a legal right to keep doing so. A neighbor's fence line, a shortcut path, a shared parking area, or an encroaching drive.",
    lookFor: [
      "Fences, driveways, sheds, or landscaping that do not line up with the mapped boundary.",
      "Worn paths, tire tracks, or utility runs crossing the lot with no recorded corridor.",
      "Neighbor statements about long-standing use, and whether that use was ever permitted in writing.",
      "State-specific time periods, which commonly range from roughly 5 to 20 years.",
    ],
    riskNote:
      "These claims are unrecorded by definition, so a title search alone will not surface them. Only a physical, on-the-ground survey compares what the record says to what is actually happening on the land. Which is why a boundary survey is the single most effective way to uncover them before closing.",
    keywords: ["prescriptive", "adverse", "unrecorded", "encroachment", "fence", "survey"],
  },
  {
    id: "title-exceptions",
    title: "Title exceptions & surveys",
    shortLabel: "Title & surveys",
    summary:
      "Schedule B of a title commitment lists the exceptions. The matters the title company will not insure. Nearly every standard owner's policy includes a general survey exception covering encroachments, overlaps, boundary line disputes, and anything an accurate survey would disclose.",
    lookFor: [
      "The standard survey exception in Schedule B, Part II, and whether it can be deleted.",
      "Whether an ALTA/NSPS Land Title Survey was ordered, and which optional Table A items were included.",
      "Recorded easements, setbacks, and covenants called out individually as exceptions.",
      "Endorsements that may restore coverage for specific survey-related risks.",
    ],
    riskNote:
      "Without an ALTA/NSPS survey, a standard owner's policy generally will not pay for a boundary shift, an encroaching structure, or a neighbor's prescriptive claim. Ordering the survey is usually what allows the title company to delete the general survey exception and actually insure the lines.",
    keywords: [
      "alta",
      "nsps",
      "schedule b",
      "exception",
      "title insurance",
      "survey",
      "endorsement",
    ],
    category: "Title, records & disputes",
  },
  {
    id: "conservation",
    title: "Conservation easements",
    shortLabel: "Conservation",
    summary:
      "A conservation easement is a voluntary, recorded agreement between a landowner and a qualified land trust or government agency that permanently limits how the land can be developed. The owner keeps title and can still sell, mortgage, or pass down the property, but the restrictions run with the land and bind every future owner. Most are perpetual, and the holder has the right to enter and monitor compliance.",
    lookFor: [
      "The recorded deed of conservation easement and the baseline documentation report describing conditions at the time of the grant.",
      "Which building envelopes, if any, were reserved. Many easements allow one homesite and nothing more.",
      "Restrictions on subdivision, timber harvest, grading, water use, signage, and commercial activity.",
      "Annual monitoring visits, reporting duties, and who pays stewardship or enforcement costs.",
      "Whether a federal charitable deduction was claimed, since IRS-qualified easements are far harder to amend.",
    ],
    riskNote:
      "Perpetual means perpetual. A buyer generally cannot renegotiate or extinguish a conservation easement, and amendment usually requires the holder's consent plus, in some states, court approval and attorney general review. Read the restrictions before you plan any construction. A barn, a second dwelling, or a pond can all be prohibited.",
    keywords: [
      "conservation",
      "land trust",
      "perpetual",
      "stewardship",
      "baseline",
      "deduction",
      "preservation",
    ],
    category: "Conservation & land protection",
  },
  {
    id: "agricultural-preservation",
    title: "Agricultural & open-space preservation easements",
    shortLabel: "Ag & open space",
    summary:
      "Purchase-of-development-rights (PDR) and farmland preservation programs pay owners to retire the right to develop working land. The parcel stays in private ownership and in production, but the development rights are permanently severed. Related tools include open-space and scenic easements, agricultural districts, and current-use tax programs.",
    lookFor: [
      "Whether development rights were sold to a county, state, or federal program, and the recorded program agreement.",
      "Minimum parcel size and anti-subdivision clauses that block splitting the farm.",
      "Right-to-farm language protecting neighboring operations from nuisance claims.",
      "Rollback taxes triggered when land leaves a current-use or agricultural-district classification.",
      "Impervious-surface caps that limit new barns, driveways, and outbuildings.",
    ],
    riskNote:
      "Preservation restrictions and tax classifications are different instruments with different consequences. A buyer who converts enrolled farmland to another use can face several years of recaptured taxes plus penalties, on top of the permanent easement restrictions.",
    keywords: [
      "agricultural",
      "farmland",
      "pdr",
      "open space",
      "rollback",
      "current use",
      "scenic",
    ],
    category: "Conservation & land protection",
  },
  {
    id: "drainage",
    title: "Drainage & stormwater easements",
    shortLabel: "Drainage",
    summary:
      "Drainage easements reserve a corridor for water to move across the lot. Swales, ditches, storm sewers, detention and retention basins, and outfalls. They are usually dedicated on the subdivision plat and held by the city, county, or drainage district, and they typically prohibit anything that would obstruct flow.",
    lookFor: [
      "Platted drainage and utility easements (often shown as D.U.E. or D.E.) and their width along rear or side lot lines.",
      "Detention or retention basins partly inside the lot, and who is responsible for mowing and repair.",
      "No-build and no-fill language that bars fences, sheds, pools, and regrading inside the corridor.",
      "FEMA flood zones, floodways, and any regulatory floodplain overlapping the easement.",
    ],
    riskNote:
      "Filling or fencing a drainage easement is one of the most common causes of enforcement letters and forced removal. The corridor is also where water is supposed to go. Improvements built next to it may flood even when the lot is technically outside the mapped flood zone.",
    keywords: ["drainage", "stormwater", "detention", "swale", "floodplain", "plat", "no-build"],
    category: "Infrastructure & corridors",
  },
  {
    id: "row-corridors",
    title: "Public right-of-way vs. private access",
    shortLabel: "Right-of-way",
    summary:
      "A public right-of-way is the corridor dedicated for a road and its shoulders, sidewalks, and utilities. Often much wider than the visible pavement. A private access easement, by contrast, is a recorded right benefiting specific parcels. Confusing the two is a frequent cause of setback, driveway, and fence disputes.",
    lookFor: [
      "The dedicated ROW width on the plat compared with the actual pavement width: 50 feet of ROW on a 22-foot road leaves a wide public strip on your frontage.",
      "Whether setbacks are measured from the ROW line or from the property line.",
      "Private road maintenance agreements, and whether the road is accepted for public maintenance.",
      "Corner clip, sight-triangle, and slope easements taken at intersections.",
    ],
    riskNote:
      "Landscaping, mailboxes, walls, and irrigation placed inside a public ROW can be removed by the road authority without compensation. Verify frontage before assuming your usable yard reaches the pavement edge.",
    keywords: ["right-of-way", "row", "road", "frontage", "setback", "dedication", "private road"],
    category: "Infrastructure & corridors",
  },
  {
    id: "solar-wind",
    title: "Solar, wind & view easements",
    shortLabel: "Solar & wind",
    summary:
      "Solar and wind easements secure access to sunlight or unobstructed wind across neighboring land, and view easements protect a sightline. Some states have statutes authorizing solar easements and limiting covenants that ban rooftop panels; leases for utility-scale solar and wind projects often add access, transmission, and setback easements across the host parcel.",
    lookFor: [
      "Recorded solar or wind easements benefiting or burdening the lot, including height and vegetation limits.",
      "Utility-scale solar or wind leases with associated road, collection line, and interconnection easements.",
      "Noise, shadow-flicker, and setback provisions in wind agreements.",
      "HOA rules on panel placement, and any state statute overriding them.",
    ],
    riskNote:
      "Sunlight and views are not automatically protected. Without a recorded easement, a neighbor can generally build or plant right up to the line and block both. And energy leases can bind the land for decades past the current ownership.",
    keywords: ["solar", "wind", "view", "sunlight", "shadow flicker", "lease", "interconnection"],
    category: "Infrastructure & corridors",
  },
  {
    id: "pipeline-mineral",
    title: "Pipeline, mineral & subsurface rights",
    shortLabel: "Pipeline & mineral",
    summary:
      "Pipeline easements let an operator install and maintain gathering or transmission lines across the lot, usually with a permanent strip plus a wider temporary construction workspace. Separately, mineral, oil, gas, and coal rights may have been severed from the surface long ago, giving the mineral owner the implied right to use the surface to reach them.",
    lookFor: [
      "The permanent easement width, depth of cover, and the operator's access and repair rights.",
      "Severed mineral reservations in the chain of title, and whether surface use is waived.",
      "Existing wells, pads, tank batteries, and access roads, plus plugging and abandonment records.",
      "Pipeline markers and one-call setbacks that restrict building and excavation near the line.",
      "Subsidence risk in areas with historic underground mining.",
    ],
    riskNote:
      "In most states the mineral estate is dominant: the mineral owner can use as much of the surface as reasonably necessary, even over the surface owner's objection. Building near or over a pipeline is typically prohibited outright and can be a life-safety issue.",
    keywords: ["pipeline", "mineral", "oil", "gas", "severed", "subsurface", "subsidence"],
    category: "Infrastructure & corridors",
  },
  {
    id: "eminent-domain",
    title: "Eminent domain & condemnation takings",
    shortLabel: "Eminent domain",
    summary:
      "Government agencies and some regulated utilities can acquire an easement or a strip of land through condemnation, paying just compensation. Road widenings, transmission lines, and pipelines frequently take a permanent easement plus a temporary construction easement rather than the entire parcel.",
    lookFor: [
      "Notices of intent, offers to purchase, or recorded condemnation judgments in the title file.",
      "Planned corridor projects in the local long-range transportation or capital improvement plan.",
      "Right-of-way reservation lines on the plat set aside for a future widening.",
      "Whether the taking is permanent, temporary, or an access-only easement, and how damages to the remainder were calculated.",
    ],
    riskNote:
      "A pending or planned taking can reduce buildable area, move the setback line, and change value long before construction starts. Compensation is negotiable and typically includes damage to the remaining land, so an appraisal and counsel are worth obtaining early.",
    keywords: [
      "eminent domain",
      "condemnation",
      "taking",
      "just compensation",
      "widening",
      "remainder",
    ],
    category: "Title, records & disputes",
  },
  {
    id: "covenants",
    title: "HOA covenants, setbacks & building restrictions",
    shortLabel: "Covenants & setbacks",
    summary:
      "Covenants, conditions, and restrictions (CC&Rs) are private rules recorded against the subdivision; setbacks and zoning are public rules imposed by the municipality. Neither is technically an easement, nobody gains a right to cross your land, but both appear in Schedule B and both limit what you can build.",
    lookFor: [
      "The recorded declaration, plat notes, and any architectural review requirements.",
      "Front, side, and rear setbacks, plus lot coverage and height limits.",
      "Whether nonconforming structures were permitted or grandfathered.",
      "Assessment obligations, liens, and the association's enforcement powers.",
    ],
    riskNote:
      "Private covenants can be stricter than zoning, and the stricter rule controls. Approval from the city does not mean approval from the HOA, and an association can force removal of work it never approved.",
    keywords: [
      "hoa",
      "ccr",
      "covenant",
      "setback",
      "zoning",
      "declaration",
      "architectural review",
    ],
    category: "Title, records & disputes",
  },
  {
    id: "necessity-implied",
    title: "Easements by necessity & implied easements",
    shortLabel: "Necessity & implied",
    summary:
      "When a parcel is landlocked by a split of commonly owned land, courts can imply an easement by necessity so the owner can reach a public road. Related doctrines include easements implied from prior use (a visible, continuous use existing at the time of severance) and easements by estoppel where an owner relied on permission to their detriment.",
    lookFor: [
      "Whether the lot has recorded, legal access to a public road. Not merely a driveway that has always been used.",
      "The common-ownership severance deed that created the landlocked condition.",
      "Visible prior uses at severance: a well line, a shared drive, a septic field on the adjoining parcel.",
      "Any written permission or license, which generally defeats a claim of right.",
    ],
    riskNote:
      "Implied rights are unrecorded and are proven in court, not at the closing table. Lenders and title companies often will not insure access based on necessity alone, so a landlocked purchase can be difficult to finance until a recorded easement is negotiated.",
    keywords: ["necessity", "implied", "landlocked", "estoppel", "severance", "access", "license"],
    category: "Core easement types",
  },
  {
    id: "termination",
    title: "Terminating, relocating & releasing an easement",
    shortLabel: "Termination",
    summary:
      "Easements can end. But rarely easily. Common routes are a recorded release from the holder, merger when one owner acquires both parcels, expiration of a stated term, abandonment shown by clear intent, or a court action to quiet title. A growing number of states also allow the burdened owner to relocate an easement if the holder's use is not materially impaired.",
    lookFor: [
      "Express termination, reversion, or expiration language in the granting document.",
      "Evidence of abandonment: removed facilities, decades of nonuse, and affirmative acts by the holder.",
      "Whether the dominant and servient parcels ever came into common ownership.",
      "Recorded releases, partial releases, and quitclaim deeds from utilities.",
    ],
    riskNote:
      "Nonuse alone almost never terminates an easement. Until a release or judgment is recorded, assume the right still exists. And get any negotiated release signed before closing rather than after.",
    keywords: [
      "terminate",
      "release",
      "abandonment",
      "merger",
      "quiet title",
      "relocation",
      "quitclaim",
    ],
    category: "Title, records & disputes",
  },
];

export type StateCountyOption = {
  /** Stable filter value: "TX" for a whole state, "TX::Travis" for a county. */
  value: string;
  label: string;
  state: string;
  county?: string;
};

/** Distinct states present in a set of listings, sorted for the filter dropdown. */
export function deriveStates(listings: DirectoryListing[]): string[] {
  return Array.from(new Set(listings.map((listing) => listing.state))).sort();
}

/** Flat State / State + County options used by the hero location dropdown. */
export function deriveStateCountyOptions(listings: DirectoryListing[]): StateCountyOption[] {
  return deriveStates(listings).flatMap((state) => {
    const counties = Array.from(
      new Set(
        listings
          .filter((listing) => listing.state === state)
          .flatMap((listing) => listing.counties),
      ),
    ).sort();

    return [
      { value: state, label: `${state}. All counties`, state },
      ...counties.map((county) => ({
        value: `${state}::${county}`,
        label: `${state}: ${county} County`,
        state,
        county,
      })),
    ];
  });
}
